/* eslint-disable no-unused-vars */
import { AppSettings } from '../../appSettings';
import {
  BaseInfo,
  Calls,
  CountryInfo,
  ManagersTable,
  Revenue,
  RevenueInfo,
  RevenueProducts,
} from '../../models/analytics';
import { UtmCategoryName } from '../../models/traffic';
import ApiClient from '../ApiClient';
import { NiceResponse } from '../dto/NiceResponse';
import ConversionManagersRequest from './dto/ConversionManagersRequest.dto';
import ConversionManagers from './dto/ConversionManagersResp.dto';
import DateRequest from './dto/DateRequest.dto';
import DealsUtm from './dto/DealsUtmResp';
import DealTypesStats from './dto/DialTypesStatsResp.dto';
import FbCreatives from './dto/GetFbCreativesResp';
import FbSpentCountry from './dto/GetFbSpentCountry.dto';
import FbSpentUrl from './dto/GetFbSpentUrl.dto';
import GetManagerStatsRequest from './dto/GetManagerStatsRequest.dto';
import ProductUrls from './dto/ProductUrls.dto';
import ProductWebinars from './dto/ProductWebinars.dto';
import RepeatSales from './dto/RepeatSales';
import TrfficStatsRequest from './dto/TrfficStatsRequest.dto';
import TrfficStats from './dto/TrfficStatsResp.dto';
import WebinarsRevenueReq from './dto/WebinarsRevenueReq.dto';
import WebinarsRevenueResp from './dto/WebinarsRevenueResp.dto';
import WebStatsRequest from './dto/WebStatsRequest.dto';
import WebStatsResp from './dto/WebStatsResp.dto';
import RevenueReportRequest from './dto/RevenueReportRequest.dto';
import { AnalyticsFirstWeb } from '../../models/analyticsFirstWeb';
import WebinarsListResponse from './dto/WebinarsListResp.dto';
import UtmsListResponse from './dto/UtmsListResp.dto';
import { DealsFirstTagTreeRequest } from './dto/DealsFirstTagTreeRequest.dto';
import { DealsFirstTagTreeResponse } from './dto/DealsFirstTagTreeResponse.dto';
import { DealsFirstTagListRequest } from './dto/DealsFirstTagListRequest.dto';
import { DealsFirstTagListResponse } from './dto/DealsFirstTagListResponse.dto';

export class AnalyticsApi {
  constructor(private api: ApiClient) {}

  path = '/analytics';

  async getRevenue(req: DateRequest) {
    const revenue = await this.api.client.post<NiceResponse<Revenue[]>>(
      this.path + '/revenue',
      { ...req },
    );
    return revenue.data;
  }

  async getRevenueProducts(req: DateRequest) {
    const revenue = await this.api.client.post<NiceResponse<RevenueProducts[]>>(
      this.path + '/revenue-products',
      { ...req },
    );
    return revenue.data;
  }

  async getRevenueInfo(req: DateRequest) {
    const revenue = await this.api.client.post<NiceResponse<RevenueInfo>>(
      this.path + '/revenue-info',
      { ...req },
    );
    return revenue.data;
  }

  async getManagersStats(req: GetManagerStatsRequest) {
    const revenue = await this.api.client.post<NiceResponse<ManagersTable[]>>(
      this.path + '/managers-table',
      {
        ...req,
        income_stages: AppSettings.income_stages,
        in_work_stages: AppSettings.in_work_stages,
        realized_stages: AppSettings.realized_stages,
        unrealized_stages: AppSettings.unrealized_stages,
      },
    );
    return revenue.data;
  }

  async getBaseData(req: DateRequest) {
    const revenue = await this.api.client.post<NiceResponse<BaseInfo>>(
      this.path + '/base-data',
      { ...req },
    );
    return revenue.data;
  }

  async getBaseTotal() {
    const revenue = await this.api.client.post<NiceResponse<BaseInfo>>(
      this.path + '/base-data-total',
    );
    return revenue.data;
  }

  async getDealTypesStats(req: GetManagerStatsRequest) {
    const revenue = await this.api.client.post<NiceResponse<DealTypesStats[]>>(
      this.path + '/deal-types-stats',
      { ...req },
    );
    return revenue.data;
  }

  async getSpendFbUrl(req: DateRequest) {
    const revenue = await this.api.client.post<NiceResponse<FbSpentUrl[]>>(
      this.path + '/spend-fb-url',
      { ...req },
    );
    return revenue.data;
  }

  async getTrafficStats(req: TrfficStatsRequest) {
    const revenue = await this.api.client.post<NiceResponse<TrfficStats>>(
      this.path + '/traffic-stats',
      { ...req },
    );
    return revenue.data;
  }

  async getRevenueCountry(req: DateRequest) {
    const revenue = await this.api.client.post<NiceResponse<CountryInfo[]>>(
      this.path + '/revenue-country',
      { ...req },
    );
    return revenue.data;
  }

  async getSpendFbCountry(req: DateRequest) {
    const revenue = await this.api.client.post<NiceResponse<FbSpentCountry[]>>(
      this.path + '/spend-fb-country',
      { ...req },
    );
    return revenue.data;
  }

  async getUniqUrls() {
    const revenue = await this.api.client.get<NiceResponse<string[]>>(
      this.path + '/get-unique-urls',
    );
    return revenue.data;
  }

  async addUrlForProducts(data: ProductUrls[]) {
    const revenue = await this.api.client.post<NiceResponse<string>>(
      this.path + '/add-url-for-product',
      { data },
    );
    return revenue.data;
  }

  async getUrlsForProducts() {
    const revenue = await this.api.client.get<NiceResponse<ProductUrls[]>>(
      this.path + '/get-all-urls-for-products',
    );
    return revenue.data;
  }

  async getWebinarNames() {
    const revenue = await this.api.client.get<NiceResponse<string[]>>(
      this.path + '/get-webinars-name',
    );
    return revenue.data;
  }

  async addWebinarForProducts(data: ProductWebinars[]) {
    const revenue = await this.api.client.post<NiceResponse<string>>(
      this.path + '/add-web-for-product',
      { data },
    );
    return revenue.data;
  }

  async getWebinarsForProducts() {
    const revenue = await this.api.client.get<NiceResponse<ProductWebinars[]>>(
      this.path + '/get-all-web-for-products',
    );
    return revenue.data;
  }

  async getWebinarsStatsByName(req: WebStatsRequest) {
    const revenue = await this.api.client.post<NiceResponse<WebStatsResp[]>>(
      this.path + '/get-webinars-stats-by-name',
      { ...req },
    );
    return revenue.data;
  }

  async getWebinarsRevenue(req: WebinarsRevenueReq) {
    const revenue = await this.api.client.post<
      NiceResponse<WebinarsRevenueResp[]>
    >(this.path + '/get-sales-for-webinars', { ...req });
    return revenue.data;
  }

  async getConversionManagers(req: ConversionManagersRequest) {
    const revenue = await this.api.client.post<
      NiceResponse<ConversionManagers[]>
    >(this.path + '/conversions-managers', { ...req });
    return revenue.data;
  }

  async getRepeatSales(endDate: string) {
    const revenue = await this.api.client.post<NiceResponse<RepeatSales>>(
      this.path + '/repeat-sales',
      { end_date: endDate },
    );
    return revenue.data;
  }

  async getCalls(req: DateRequest) {
    const resp = await this.api.client.post<NiceResponse<Calls[]>>(
      this.path + '/calls',
      { ...req },
    );
    return resp.data;
  }

  async getUtms(type: UtmCategoryName, searchText: string) {
    const resp = await this.api.client.get<NiceResponse<string[]>>(
      this.path + '/utms',
      { params: { type, searchText } },
    );
    return resp.data;
  }

  async getFbCreatives(startDate: string, endDate: string) {
    const resp = await this.api.client.get<NiceResponse<FbCreatives[]>>(
      this.path + '/creatives-fb',
      { params: { startDate, endDate } },
    );
    return resp.data;
  }

  async getDealsUtm(
    startDate: string,
    endDate: string,
    parent: string | null,
    name: string | null,
  ) {
    const params: {
      startDate: string;
      endDate: string;
      parent?: string | null;
      name?: string | null;
    } = {
      startDate,
      endDate,
    };

    if (parent) {
      params.parent = parent;
    }

    if (parent && name) {
      params.name = name;
    }

    const resp = await this.api.client.get<NiceResponse<DealsUtm[]>>(
      this.path + '/deals-by-utm-sources',
      { params: params },
    );
    return resp.data;
  }

  async getRevenueReport(req: RevenueReportRequest) {
    const resp = await this.api.client.post<NiceResponse<AnalyticsFirstWeb>>(
      this.path + '/revenue-report',
      { ...req },
    );
    return resp.data;
  }

  async getWebinarsList(params?: {
    text?: string;
    skip?: number;
    limit?: number;
  }) {
    const resp = await this.api.client.get<NiceResponse<WebinarsListResponse>>(
      this.path + '/webinars-list',
      { params: params || {} },
    );
    return resp.data;
  }

  async getUtmsList(params: {
    type:
      | 'utm_source'
      | 'utm_medium'
      | 'utm_campaign'
      | 'utm_term'
      | 'utm_content';
    text?: string;
    skip?: number;
    limit?: number;
  }) {
    const resp = await this.api.client.get<NiceResponse<UtmsListResponse>>(
      this.path + '/utms-list',
      { params },
    );
    return resp.data;
  }

  async getDealsFirstTagTree(req: DealsFirstTagTreeRequest) {
    const resp = await this.api.client.post<DealsFirstTagTreeResponse>(
      this.path + '/deals-first-tag/tree',
      { ...req },
    );
    return resp.data;
  }

  async getDealsFirstTagList(req: DealsFirstTagListRequest) {
    const resp = await this.api.client.post<DealsFirstTagListResponse>(
      this.path + '/deals-first-tag/list',
      { ...req },
    );
    return resp.data;
  }
}
